import { Alert, Button, Checkbox, Chip, Group, Loader, Text } from '@mantine/core';
import { IconMicrophone } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { MemoryCard } from 'src/Components/Memories/MemoryCard';
import { MEMORY_KINDS, memoryKindLabel } from 'src/Components/Memories/memoryKinds';
import { PageHeader } from 'src/Components/Navigation/PageHeader';
import useFetchMemories from 'src/Hooks/Queries/useFetchMemories';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { MemoryKind, MemoryTimelineItemDto } from 'src/Services/API/Memories.api';
import { trackMemoryTimelineViewed } from 'src/Services/Mixpanel/events';
import { decadeLabel, decadeOf } from 'src/Utils/memoryDate';

import styles from './MemoriesTimelinePage.module.scss';

const CURRENT_DECADE = Math.floor(new Date().getFullYear() / 10) * 10;
const UNDATED = 'undated';

const sectionId = (decade: number | typeof UNDATED) => `memories-${decade}`;

const MemoriesTimelinePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();
  const [kind, setKind] = useState<MemoryKind | 'ALL'>('ALL');
  const [withVoice, setWithVoice] = useState(false);
  const { data, isLoading, isError } = useFetchMemories({
    kind: kind === 'ALL' ? undefined : kind,
    withVoice,
  });
  const isFiltered = kind !== 'ALL' || withVoice;

  useEffect(() => {
    if (data) trackMemoryTimelineViewed({ memory_count: data.stats.memoryCount });
    // Once per visit, not on every filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!data]);

  // Decades from the oldest dated memory to today; empty ones invite someone to tell them.
  const { decades, byDecade, undated } = useMemo(() => {
    const groups = new Map<number, MemoryTimelineItemDto[]>();
    const withoutDate: MemoryTimelineItemDto[] = [];
    for (const item of data?.items ?? []) {
      const decade = decadeOf(item.takenAt);
      if (decade === null) withoutDate.push(item);
      else groups.set(decade, [...(groups.get(decade) ?? []), item]);
    }
    const firstYear = data?.stats.firstYear;
    const first = firstYear ? Math.floor(firstYear / 10) * 10 : null;
    const range: number[] = [];
    if (first !== null) {
      for (let decade = first; decade <= CURRENT_DECADE; decade += 10) {
        // A filtered view only shows the decades it has something for.
        if (!isFiltered || groups.has(decade)) range.push(decade);
      }
    }
    return { decades: range, byDecade: groups, undated: withoutDate };
  }, [data, isFiltered]);

  const tellMemory = (decade?: number) =>
    navigate(`/asset/${assetId}/memories/new${decade ? `?decade=${decade}` : ''}`);

  const decadeName = (decade: number) =>
    decade < 2000 ? String(decade).slice(-2) : String(decade);
  const pillLabel = (decade: number) =>
    decade === CURRENT_DECADE
      ? t('memories.timeline.today', "Aujourd'hui")
      : t('memories.decadeShort', '{{decade}}', { decade: decadeName(decade) });

  const scrollTo = (decade: number | typeof UNDATED) =>
    document.getElementById(sectionId(decade))?.scrollIntoView({ behavior: 'smooth' });

  const stats = data?.stats;
  const voiceMinutes = Math.round((stats?.voiceSeconds ?? 0) / 60);

  return (
    <div className={styles.container}>
      <PageHeader
        label={t('memories.title', 'La mémoire de la maison')}
        rightElement={
          <Button size="md" leftSection={<IconMicrophone size={20} />} onClick={() => tellMemory()}>
            {t('memories.tell', 'Raconter un souvenir')}
          </Button>
        }
      />

      {stats && stats.memoryCount > 0 && (
        <Text className={styles.stats}>
          {[
            stats.firstYear
              ? t('memories.stats.since', 'Depuis {{year}}', { year: stats.firstYear })
              : null,
            t('memories.stats.memories', {
              count: stats.memoryCount,
              defaultValue: '{{count}} souvenirs',
            }),
            t('memories.stats.photos', {
              count: stats.photoCount,
              defaultValue: '{{count}} photos',
            }),
            voiceMinutes > 0
              ? t('memories.stats.voice', '{{minutes}} min de voix', { minutes: voiceMinutes })
              : null,
            stats.storytellerCount > 0
              ? t('memories.stats.storytellers', {
                  count: stats.storytellerCount,
                  defaultValue: '{{count}} conteurs',
                })
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
      )}

      {stats && stats.memoryCount > 0 && (
        <div className={styles.toolbar}>
          {decades.length > 0 && (
            <Group gap="xs" className={styles.decades}>
              {decades.map(decade => (
                <Button key={decade} variant="light" size="sm" onClick={() => scrollTo(decade)}>
                  {pillLabel(decade)}
                </Button>
              ))}
              {undated.length > 0 && (
                <Button variant="light" color="gray" size="sm" onClick={() => scrollTo(UNDATED)}>
                  {t('memories.timeline.undatedShort', 'Sans date')}
                </Button>
              )}
            </Group>
          )}
          <Group gap="xs" align="center">
            <Chip.Group value={kind} onChange={value => setKind(value as MemoryKind | 'ALL')}>
              <Chip value="ALL" size="md">
                {t('memories.filters.all', 'Tout')}
              </Chip>
              {MEMORY_KINDS.map(memoryKind => (
                <Chip key={memoryKind} value={memoryKind} size="md">
                  {memoryKindLabel(t, memoryKind)}
                </Chip>
              ))}
            </Chip.Group>
            <Checkbox
              size="md"
              label={t('memories.filters.withVoice', 'Avec la voix')}
              checked={withVoice}
              onChange={event => setWithVoice(event.currentTarget.checked)}
            />
          </Group>
        </div>
      )}

      {isLoading && <Loader />}
      {isError && !data && (
        <Alert color="red">
          {t(
            'memories.timeline.error',
            "La mémoire de la maison n'a pas pu être chargée. Réessayez dans un instant."
          )}
        </Alert>
      )}

      {stats && stats.memoryCount === 0 && (
        <div className={styles.gapCard}>
          <Text className={styles.gapTitle}>
            {t('memories.empty.title', 'Racontez le premier souvenir de la maison')}
          </Text>
          <Text c="dimmed">
            {t(
              'memories.empty.subtitle',
              'Un mariage dans le jardin, la tempête de 1999, la véranda construite par grand-père : chaque histoire compte.'
            )}
          </Text>
          <Button size="md" onClick={() => tellMemory()}>
            {t('memories.tell', 'Raconter un souvenir')}
          </Button>
        </div>
      )}

      {data && data.items.length === 0 && isFiltered && stats && stats.memoryCount > 0 && (
        <Text c="dimmed">{t('memories.filters.noResult', 'Aucun souvenir pour ce filtre.')}</Text>
      )}

      <div className={styles.timeline}>
        {decades.map(decade => {
          const items = byDecade.get(decade) ?? [];
          return (
            <section key={decade} id={sectionId(decade)} className={styles.decade}>
              <h2 className={styles.decadeTitle}>{decadeLabel(t, decade)}</h2>
              {items.length === 0 ? (
                <div className={styles.gapCard}>
                  <Text className={styles.gapTitle}>
                    {t(
                      'memories.timeline.gap',
                      "Personne n'a encore raconté les années {{decade}}",
                      {
                        decade: decadeName(decade),
                      }
                    )}
                  </Text>
                  <Button variant="light" size="md" onClick={() => tellMemory(decade)}>
                    {t('memories.tell', 'Raconter un souvenir')}
                  </Button>
                </div>
              ) : (
                items.map(item => (
                  <MemoryCard
                    key={item.id}
                    memory={item}
                    onClick={() => navigate(`/asset/${assetId}/memories/${item.id}`)}
                  />
                ))
              )}
            </section>
          );
        })}

        {undated.length > 0 && (
          <section id={sectionId(UNDATED)} className={styles.decade}>
            <h2 className={styles.decadeTitle}>
              {t('memories.timeline.undated', 'Sans date — vous vous en souvenez ?')}
            </h2>
            {undated.map(item => (
              <MemoryCard
                key={item.id}
                memory={item}
                onClick={() => navigate(`/asset/${assetId}/memories/${item.id}`)}
              />
            ))}
          </section>
        )}
      </div>
    </div>
  );
};

export default MemoriesTimelinePage;
